import React, { useEffect, useMemo, useState } from "react";
import {
  // makeStyles,
  // Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from "@material-ui/core";
import OtpInput from "react-otp-input";
import { PopUp } from "../../BasePopUp";
import authService from "src/services/authService";
import { useLayout, useTimeleft } from "src/utils/customHook";
import BaseDrawer from "../../BaseDrawer";
import { banOTPErrorType } from "src/types/error-types";
import { ERROR_CODE } from "src/context/constants/errorCode";
import useRequestRegisPhoneNo from "src/hooks/rest/useRequestRegisPhoneNo";
import useRequestResetPassword from "src/hooks/rest/useRequestResetPassword";

type ConfirmType = "requestRegisPhoneNo" | "requestResetPassword";

interface OTPConfirmPopupProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  phoneNumber?: string;
  brokerID?: string;
  onSuccess?: () => void;
  onFail?: () => void;
  onBack?: () => void;
  confirmType?: ConfirmType;
}

var timer: any = undefined;
const OTP_INTERVAL = 30;

const OTPConfirmPopup: React.FC<OTPConfirmPopupProps> = ({
  onClose,
  open,
  title,
  phoneNumber,
  brokerID,
  onSuccess,
  onFail,
  onBack,
  confirmType = "requestRegisPhoneNo",
}) => {
  const theme = useTheme();
  const { isSmall } = useLayout();
  const { requestRegisPhoneNo } = useRequestRegisPhoneNo();
  const { requestResetPassword } = useRequestResetPassword();
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const { timeLeft, restartCount } = useTimeleft(OTP_INTERVAL);

  const stopTimer = () => {
    clearInterval(timer);
  };

  const timeLeftMessage = useMemo(() => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft - 60 * minutes;
    if (minutes > 0) return `${minutes} นาที ${seconds} วินาที`;
    return `${seconds} วินาที`;
  }, [timeLeft]);

  const banErrorCallback = (err: banOTPErrorType) => {
    if (err.code === ERROR_CODE.OTP_BAN) {
      if (err.second) restartCount(err.second);
    }
  };

  useEffect(() => {
    if (open) {
      setOtp("");
      setOtpError(false);
      restartCount(OTP_INTERVAL);
      setLoading(false);
    }
    return () => {
      stopTimer();
    };
    // eslint-disable-next-line
  }, [open]);

  const handleOTPChange = (otp: string) => {
    setOtp(otp);
    setOtpError(false);
  };

  const onClickSendAgain = async () => {
    try {
      setLoading(true);
      if (!brokerID) return;
      if (!phoneNumber) return;
      switch (confirmType) {
        case "requestRegisPhoneNo":
          const regisPhoneNoData = await requestRegisPhoneNo(
            brokerID,
            phoneNumber
          );
          if (regisPhoneNoData.code === ERROR_CODE.OTP_BAN)
            return banErrorCallback(regisPhoneNoData.error);
          return restartCount(OTP_INTERVAL);
        case "requestResetPassword":
          const resetPasswordData = await requestResetPassword(
            brokerID,
            phoneNumber
          );
          if (resetPasswordData.code === ERROR_CODE.OTP_BAN)
            return banErrorCallback(resetPasswordData.error);
          return restartCount(OTP_INTERVAL);
        default:
          return;
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      setOtpError(false);
      setLoading(true);
      await authService.verifyOTP(otp);
      setLoading(false);
      if (onSuccess) await onSuccess();
      // onClose();
    } catch (err) {
      setOtpError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 4) verifyOTP();
    // eslint-disable-next-line
  }, [otp]);
  const renderDonotAccept = (
    <>
      <Typography variant="body2" color="textSecondary">
        ไม่ได้รับข้อความ OTP ?
      </Typography>
      <Button
        color="primary"
        disabled={timeLeft > 0 || loading}
        onClick={onClickSendAgain}
        style={{ paddingTop: 0, paddingBottom: 0 }}
      >
        ส่งอีกครั้ง
      </Button>
    </>
  );
  const renderEnterOTP = (
    <>
      <Typography variant="body2" color="textSecondary" align="center">
        ไม่ได้รับข้อความ OTP ? สามารถส่งอีกครั้งใน{" "}
        <span
          style={{
            color: theme.palette.primary.main,
          }}
        >{`${timeLeftMessage}`}</span>{" "}
      </Typography>
    </>
  );

  const RenderOtp = (
    <>
      <Box m={3} />
      <Box mx="auto">
        <Box display="flex" alignItems="center" justifyContent="center">
          <OtpInput
            inputStyle={{
              color: theme.palette.text.primary,
              background: theme.palette.background.default,
              borderStyle: "solid solid solid solid",
              width: theme.spacing(7),
              height: theme.spacing(7),
              borderColor: theme.palette.text.secondary,
              borderRadius: "10px",
              borderWidth: "1px",
              margin: theme.spacing(1.25),
              fontSize: theme.spacing(2),
            }}
            errorStyle={{
              color: theme.palette.secondary.main,
              borderColor: theme.palette.secondary.main,
            }}
            focusStyle={{
              outline: "-webkit-focus-ring-color auto 0px",
              borderColor: theme.palette.primary.main,
            }}
            shouldAutoFocus={true}
            value={otp}
            onChange={handleOTPChange}
            numInputs={4}
            separator={<span />}
            isInputNum={true}
            hasErrored={otpError}
            isDisabled={loading}
          />
        </Box>
        <Box mt={2} />
        {otpError && (
          <Box display="flex" flex={1} justifyContent="center">
            <Typography variant="caption" color="secondary" align="center">
              รหัส OTP ไม่ถูกต้องโปรดลองใหม่
            </Typography>
          </Box>
        )}
        <Box display="flex" mt={2} alignItems="center" justifyContent="center">
          {timeLeft > 0 ? renderEnterOTP : renderDonotAccept}
        </Box>
        <Box mt={6} />
        {loading && <CircularProgress />}
      </Box>
    </>
  );

  if (isSmall)
    return (
      <BaseDrawer
        title={title || "ยืนยันหมายเลขโทรศัพท์"}
        open={open}
        onClose={onClose}
        onBack={onBack}
        anchor="right"
      >
        {RenderOtp}
      </BaseDrawer>
    );

  return (
    <PopUp
      title={title || "ยืนยันหมายเลขโทรศัพท์"}
      open={open}
      handleClose={onClose}
      onBack={onBack}
    >
      {RenderOtp}
    </PopUp>
  );
};

export default OTPConfirmPopup;
